import { getDefaultStore, type KeyValueStore, prefixedKey } from "./storage";

export type Validator<T> = (value: unknown) => value is T;
export type Migration = (prev: unknown) => unknown;

export interface PersistedRecord<T> {
  readonly key: string;
  readonly version: number;
  readonly defaults: () => T;
  readonly validate: Validator<T>;
  readonly migrations: Readonly<Record<number, Migration>>;
}

interface PersistedEnvelope {
  readonly version: number;
  readonly data: unknown;
}

export class PersistentStore<T> {
  private readonly storageKey: string;

  constructor(
    private readonly record: PersistedRecord<T>,
    private readonly storage: KeyValueStore = getDefaultStore(),
  ) {
    if (!Number.isInteger(record.version) || record.version < 1) {
      throw new Error(
        `PersistedRecord.version must be a positive integer, got ${String(record.version)}`,
      );
    }
    this.storageKey = prefixedKey(record.key);
  }

  load(): T {
    const raw = this.storage.read(this.storageKey);
    if (raw === null) return this.record.defaults();

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return this.record.defaults();
    }
    if (!isEnvelope(parsed)) return this.record.defaults();

    let migrated: unknown;
    try {
      migrated = this.migrate(parsed);
    } catch {
      return this.record.defaults();
    }
    if (!this.record.validate(migrated)) return this.record.defaults();
    return migrated;
  }

  save(value: T): void {
    const envelope: PersistedEnvelope = {
      version: this.record.version,
      data: value,
    };
    this.storage.write(this.storageKey, JSON.stringify(envelope));
  }

  clear(): void {
    this.storage.remove(this.storageKey);
  }

  private migrate(envelope: PersistedEnvelope): unknown {
    const target = this.record.version;
    if (envelope.version > target) {
      throw new Error(
        `stored version ${String(envelope.version)} exceeds current ${String(target)}`,
      );
    }
    let version = envelope.version;
    let body: unknown = envelope.data;
    while (version < target) {
      const step = this.record.migrations[version];
      if (!step) {
        throw new Error(`missing migration from version ${String(version)}`);
      }
      body = step(body);
      version += 1;
    }
    return body;
  }
}

function isEnvelope(value: unknown): value is PersistedEnvelope {
  if (typeof value !== "object" || value === null) return false;
  if (!("version" in value) || !("data" in value)) return false;
  const version = (value as { version: unknown }).version;
  return typeof version === "number" && Number.isInteger(version) && version >= 1;
}
